/**
 * Invoice Template System - Type Definitions
 * 
 * This file contains all TypeScript interfaces for the visual invoice template builder.
 * Based on analysis of mid-market PSA solutions (Kantata, Projector, Certinia, etc.)
 */

// ============================================================================
// CORE TYPES
// ============================================================================

export type SectionType = 'header' | 'summary' | 'line-items' | 'totals' | 'footer' | 'custom';

export type GroupByOption = 'none' | 'person' | 'phase' | 'task' | 'role' | 'cost-center';

export type LogoPosition = 'left' | 'center' | 'right';

export type Alignment = 'left' | 'center' | 'right';

export type ColumnField = 
  | 'date'
  | 'description'
  | 'costCentreCode'
  | 'costCentreName'
  | 'person'
  | 'role'
  | 'phase'
  | 'task'
  | 'qty'
  | 'net'
  | 'vat'
  | 'vatCode'
  | 'amount'
  | 'notes'
  | 'type';

// ============================================================================
// INVOICE TEMPLATE
// ============================================================================

export interface InvoiceTemplate {
  id: number;
  name: string;
  description: string;
  isDefault: boolean;
  
  // Sections (ordered array)
  sections: TemplateSection[];
  
  // Branding
  branding: TemplateBranding;
  
  // Settings
  settings: TemplateSettings;
  
  // Metadata
  createdAt: Date;
  updatedAt: Date;
  createdBy?: string;
}

// ============================================================================
// BRANDING
// ============================================================================

export interface TemplateBranding {
  logo?: string; // URL or base64
  logoPosition: LogoPosition;
  logoWidth?: number; // pixels
  primaryColor: string; // hex color
  secondaryColor: string; // hex color
  textColor: string; // hex color
  fontFamily: string;
  fontSize: number; // base font size in px
}

// ============================================================================
// SETTINGS
// ============================================================================

export interface TemplateSettings {
  // Grouping
  groupBy: GroupByOption;
  showIndividualEntries: boolean;
  
  // Formatting
  dateFormat: string; // e.g., "MM/DD/YYYY", "DD/MM/YYYY"
  currencyFormat: string; // e.g., "USD", "EUR", "GBP"
  numberFormat: string; // e.g., "1,234.56", "1.234,56"
  
  // Layout
  pageSize: 'A4' | 'Letter';
  orientation: 'portrait' | 'landscape';
  margins: {
    top: number;
    right: number;
    bottom: number;
    left: number;
  };
}

// ============================================================================
// SECTIONS
// ============================================================================

export interface TemplateSection {
  id: string; // unique ID for drag-and-drop
  type: SectionType;
  order: number;
  visible: boolean;
  
  // Section-specific configuration
  config: SectionConfig;
}

export type SectionConfig = 
  | HeaderSectionConfig
  | SummarySectionConfig
  | LineItemsSectionConfig
  | TotalsSectionConfig
  | FooterSectionConfig
  | CustomSectionConfig;

// ============================================================================
// HEADER SECTION
// ============================================================================

export interface HeaderSectionConfig {
  type: 'header';
  
  // Company info
  showCompanyName: boolean;
  showCompanyAddress: boolean;
  showCompanyPhone: boolean;
  showCompanyEmail: boolean;
  showVAT: boolean;
  showRegistration: boolean;
  
  // Invoice info
  showInvoiceNumber: boolean;
  showInvoiceDate: boolean;
  showDueDate: boolean;
  showPONumber: boolean;
  
  // Client info
  showClientName: boolean;
  showClientAddress: boolean;
  showClientContact: boolean;
  
  // Layout
  layout: 'single-column' | 'two-column';
}

// ============================================================================
// SUMMARY SECTION
// ============================================================================

export interface SummarySectionConfig {
  type: 'summary';
  
  // What to show
  showProjectName: boolean;
  showProjectDescription: boolean;
  showBillingPeriod: boolean;
  showTotalHours: boolean;
  showTotalAmount: boolean;
  
  // Custom text
  customText?: string; // Supports merge fields like {project.name}
}

// ============================================================================
// LINE ITEMS SECTION
// ============================================================================

export interface LineItemsSectionConfig {
  type: 'line-items';
  
  // Grouping
  groupBy: GroupByOption;
  showGroupSubtotals: boolean;
  expandable: boolean; // Can groups be collapsed?
  
  // Columns
  columns: ColumnConfig[];

  /**
   * The display order of columns in the invoice table.
   * Columns not present in this array are rendered after those that are.
   * Defaults to: date, description, person, role, qty, rate, vat, vatCode, amount
   * Task 2.6 — Phase 2
   */
  columnOrder?: ColumnField[];

  /**
   * Rich text HTML template for the Description cell of each grouped TIMESHEET summary row.
   * Supports inline formatting (bold, italic) and token substitution via resolveGroupLabel().
   *
   * Example: "<p>Services rendered by <strong>{person}</strong></p>"
   *
   * Available tokens are filtered to the active groupBy (see GROUP_BY_TOKENS in invoice-group-utils.ts).
   * Falls back to group.groupLabel (the default token for the active groupBy) when empty/undefined.
   *
   * Only relevant when groupBy !== 'none'.
   * Task 3.7 — Phase 3
   */
  groupedRowDescription?: string;

  /**
   * Rich text HTML template for the Description cell of each grouped EXPENSE summary row.
   * Supports tokens: {costCentreName}, {costCentreCode}, {phase}, {qty}, {subtotal}, {itemCount}.
   * Falls back to group.groupLabel when empty/undefined.
   */
  groupedRowDescriptionExpense?: string;

  /**
   * Rich text HTML template for the Description cell of each grouped PURCHASE summary row.
   * Supports tokens: {costCentreName}, {costCentreCode}, {qty}, {subtotal}, {itemCount}.
   * Falls back to group.groupLabel when empty/undefined.
   */
  groupedRowDescriptionPurchase?: string;

  /**
   * When true, a Backing Sheet is appended after the main invoice.
   * The Backing Sheet shows full flat (never grouped) line-by-line detail rows
   * with a repeated invoice header and no footer. Available regardless of groupBy.
   * Task 5.1 / 5.6 — Phase 5
   */
  includeBackingSheet?: boolean;

  /**
   * Independent column visibility for the Backing Sheet.
   * Keys are ColumnField values; true = show, false = hide.
   * When undefined, all columns default to visible (true).
   * Task 5.7 — Phase 5
   */
  backingSheetColumns?: Partial<Record<ColumnField, boolean>>;

  /**
   * Display order of columns on the Backing Sheet — independent from the main invoice order.
   * Defaults to the same order as columnOrder (main invoice) when first enabled.
   * Task 5.7 — Phase 5
   */
  backingSheetColumnOrder?: ColumnField[];

  /**
   * Page orientation for the Backing Sheet — independent from the main invoice orientation.
   * Defaults to 'landscape' to accommodate wider column sets.
   * Task 5.7 — Phase 5
   */
  backingSheetOrientation?: 'portrait' | 'landscape';

  // Styling
  showBorders: boolean;
  alternateRowColors: boolean;
  headerBackgroundColor?: string;
}

export interface ColumnConfig {
  id: string;
  field: ColumnField;
  label: string;
  visible: boolean;
  width: number; // percentage (0-100)
  alignment: Alignment;
  format?: string; // for dates, numbers, currency
}

// ============================================================================
// TOTALS SECTION
// ============================================================================

export interface TotalsSectionConfig {
  type: 'totals';
  
  // What to show
  showSubtotal: boolean;
  showTax: boolean;
  showDiscount: boolean;
  showTotal: boolean;
  
  // Tax configuration
  taxLabel?: string; // e.g., "GST", "VAT", "Sales Tax"
  taxRate?: number; // percentage
  
  // Discount configuration
  discountLabel?: string;
  discountType?: 'percentage' | 'fixed';
  discountValue?: number;
  
  // Styling
  alignment: Alignment;
  showCurrency: boolean;
}

// ============================================================================
// FOOTER SECTION
// ============================================================================

export interface FooterSectionConfig {
  type: 'footer';
  
  // What to show
  showBankDetails: boolean;
  showNotes: boolean;
  showTermsAndConditions: boolean;
  
  // Content
  paymentTerms?: string;
  bankDetails?: string;
  notes?: string;
  termsAndConditions?: string;
  
  // Layout
  layout: 'single-column' | 'two-column';
}

// ============================================================================
// CUSTOM SECTION
// ============================================================================

export interface CustomSectionConfig {
  type: 'custom';
  
  // Content
  title?: string;
  content: string; // Supports HTML and merge fields
  
  // Styling
  backgroundColor?: string;
  textColor?: string;
  padding?: number;
}

// ============================================================================
// TEMPLATE LIBRARY
// ============================================================================

export interface TemplateLibraryItem {
  id: string;
  name: string;
  description: string;
  category: 'professional-services' | 'legal' | 'consulting' | 'it-services' | 'general';
  thumbnail: string; // URL to preview image
  template: InvoiceTemplate;
}

// ============================================================================
// PREVIEW DATA
// ============================================================================

export interface InvoicePreviewData {
  // Company
  company: {
    name: string;
    address: string;
    phone: string;
    email: string;
    logo?: string;
    vatNumber?: string;
    registrationNumber?: string;
    bankDetails?: {
      bankName?: string;
      accountName?: string;
      sortCode?: string;
      accountNumber?: string;
      iban?: string;
      swiftBic?: string;
    };
  };
  
  // Invoice
  invoice: {
    number: string;
    date: string;
    dueDate: string;
    poNumber?: string;
  };
  
  // Client
  client: {
    name: string;
    address: string;
    contact: string;
  };
  
  // Project
  project: {
    name: string;
    description: string;
    projectManager?: string;
  };
  
  // Billing period
  billingPeriod: {
    start: string;
    end: string;
  };
  
  // Line items
  lineItems: InvoiceLineItem[];
  
  // Totals
  totals: {
    subtotal: number;
    tax: number;
    discount: number;
    total: number;
  };
}

export type LineItemType = 'timesheet' | 'expense' | 'purchase';

export interface InvoiceLineItem {
  id: string;
  /**
   * Discriminates between timesheet, expense, and purchase rows.
   * Timesheets have person/role; expenses and purchases do not.
   * Defaults to 'timesheet' when absent (backwards-compatibility).
   */
  lineItemType?: LineItemType;
  /**
   * Human-readable transaction type label for the "Type" column (e.g. 'Time',
   * 'Purchase', 'Invoice', 'Journal', 'VAT Journal'). Unlike `lineItemType`
   * (a coarse enum used for grouping), this preserves the exact WIP source
   * type so the invoice reflects the actual transaction type.
   */
  typeLabel?: string;
  date: string;
  description: string;
  projectCode?: string;
  projectName?: string;
  costCentreCode: string;
  costCentreName: string;
  person: string; // Only for timesheet entries, blank for expenses/purchases
  role: string; // Only for timesheet entries, blank for expenses/purchases
  phase: string;
  task: string;
  qty: number; // Hours for time entries, unit count for purchases/expenses
  rate?: number; // Bill rate per unit (used in preview)
  net: number; // Net amount before VAT
  vat: number; // VAT amount
  vatCode: string; // VAT code (e.g., "S" for standard, "Z" for zero-rated)
  amount: number; // Total amount including VAT
  notes?: string;
}

// ============================================================================
// BUILDER STATE
// ============================================================================

export interface TemplateBuilderState {
  template: InvoiceTemplate;
  selectedSectionId: string | null;
  previewMode: 'desktop' | 'mobile';
  previewData: InvoicePreviewData;
  isDirty: boolean;
}

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

export const DEFAULT_BRANDING: TemplateBranding = {
  logoPosition: 'left',
  logoWidth: 150,
  primaryColor: '#2563eb', // blue-600
  secondaryColor: '#64748b', // slate-500
  textColor: '#1e293b', // slate-800
  fontFamily: 'Inter, system-ui, sans-serif',
  fontSize: 14,
};

export const DEFAULT_SETTINGS: TemplateSettings = {
  groupBy: 'none',
  showIndividualEntries: true,
  dateFormat: 'MM/DD/YYYY',
  currencyFormat: 'USD',
  numberFormat: '1,234.56',
  pageSize: 'Letter',
  orientation: 'portrait',
  margins: {
    top: 72, // 1 inch
    right: 72,
    bottom: 72,
    left: 72,
  },
};

export const DEFAULT_COLUMNS: ColumnConfig[] = [
  {
    id: 'date',
    field: 'date',
    label: 'Date',
    visible: true,
    width: 12,
    alignment: 'left',
    format: 'MM/DD/YYYY',
  },
  {
    id: 'description',
    field: 'description',
    label: 'Description',
    visible: true,
    width: 43,
    alignment: 'left',
  },
  {
    id: 'person',
    field: 'person',
    label: 'Person',
    visible: true,
    width: 20,
    alignment: 'left',
  },
  {
    id: 'qty',
    field: 'qty',
    label: 'Qty',
    visible: true,
    width: 10,
    alignment: 'right',
    format: '0.00',
  },
  {
    id: 'net',
    field: 'net',
    label: 'Net',
    visible: true,
    width: 15,
    alignment: 'right',
    format: '£0,0.00',
  },
  {
    id: 'amount',
    field: 'amount',
    label: 'Amount',
    visible: true,
    width: 0, // auto — fills remaining space
    alignment: 'right',
    format: '£0,0.00',
  },
];

export function createDefaultTemplate(name: string = 'New Template'): InvoiceTemplate {
  return {
    id: Date.now(),
    name,
    description: '',
    isDefault: false,
    sections: [
      {
        id: 'header-1',
        type: 'header',
        order: 0,
        visible: true,
        config: {
          type: 'header',
          showCompanyName: true,
          showCompanyAddress: true,
          showCompanyPhone: true,
          showCompanyEmail: true,
          showVAT: true,
          showRegistration: true,
          showInvoiceNumber: true,
          showInvoiceDate: true,
          showDueDate: true,
          showPONumber: false,
          showClientName: true,
          showClientAddress: true,
          showClientContact: true,
          layout: 'two-column',
        },
      },
      {
        id: 'line-items-1',
        type: 'line-items',
        order: 1,
        visible: true,
        config: {
          type: 'line-items',
          groupBy: 'none',
          showGroupSubtotals: false,
          expandable: false,
          columns: DEFAULT_COLUMNS,
          showBorders: true,
          alternateRowColors: true,
          headerBackgroundColor: '#f1f5f9',
        },
      },
      {
        id: 'totals-1',
        type: 'totals',
        order: 2,
        visible: true,
        config: {
          type: 'totals',
          showSubtotal: true,
          showTax: true,
          showDiscount: false,
          showTotal: true,
          taxLabel: 'Tax',
          taxRate: 0,
          alignment: 'right',
          showCurrency: true,
        },
      },
      {
        id: 'footer-1',
        type: 'footer',
        order: 3,
        visible: true,
        config: {
          type: 'footer',
          showBankDetails: false,
          showNotes: true,
          showTermsAndConditions: false,
          paymentTerms: 'Payment due within 30 days',
          notes: 'Thank you for your business!',
          layout: 'single-column',
        },
      },
    ],
    branding: DEFAULT_BRANDING,
    settings: DEFAULT_SETTINGS,
    createdAt: new Date(),
    updatedAt: new Date(),
  };
}
